import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Checkbox, Collapse, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { PaginationProps, TableCellHeaderSortOptions } from 'app/components';
import { Pagination } from 'app/components';
import TableCellHeader from 'app/components/TableCellHeader';
import type { BatchData, BatchDataFieldType, BatchDataFieldValue } from 'app/types';
import { ConnectionSourcePackageTypeEnum } from 'app/types';
import { TranslationKeys } from 'app/translations';
import formatter from 'app/utils/formatter';
import { useLocale } from 'app/components/Intl';
import { CompareBatchTableToolbar, CompareBatchTableInnerRow } from './components';
import { useConnectionGetConnectionQuery } from '../../../../../../../../hooks';
import { TableContainerStyled, TableRowStyled, NoDataRowStyled } from './CompareBatchTable.styles';

export const COMPARE_BATCH_TABLE_DATA_TEST_ID = 'compare_batch_table_component';

type CompareBatchTableColumn = {
  name: string;
  label: React.ReactNode;
  sortable?: boolean;
  whiteSpace?: 'nowrap' | 'normal';
  width?: string | number;
};

type CompareBatchTableProps = {
  batchId?: string;
  connectionId: string;
  synchronizeWithInsurer: () => void;
  batchCompareData: BatchData[];
  selected: Set<string>;
  onSelect: (selected: Set<string>) => void;
  onSearch: (inputValue: string) => void;
  onFilterMutationType: (filter: string) => void;
  onSortChange: (sortBy: Record<string, TableCellHeaderSortOptions>) => void;
  sortBy: Record<string, TableCellHeaderSortOptions>;
  approveBatch: () => void;
  archiveBatch: () => void;
  markAsComplete: () => void;
  markSelectedAsComplete: () => void;
  resendMutations: () => void;
  rejectMutations: () => void;
  declineBatch: () => void;
  paginationProps: PaginationProps;
  isLoading?: boolean;
  searchQuery?: string;
  batchStatus: 'draft' | 'pending-insurer-data' | 'compared' | 'to-be-checked';
  mutationTypes: string[];
};

type CollapsibleRowProps = {
  data: BatchData;
  isSelected: boolean;
  onSelectHandler: () => void;
  isConnectionManualInput: boolean;
  getFormattedFieldValue: (params: { type: BatchDataFieldType; value: BatchDataFieldValue }) => React.ReactNode;
  batchId?: string;
  connectionId: string;
  intl: ReturnType<typeof useIntl>;
  columns: CompareBatchTableColumn[];
};

const CollapsibleRow = ({
  data,
  isSelected,
  onSelectHandler,
  getFormattedFieldValue,
  batchId,
  connectionId,
  intl,
  columns,
}: CollapsibleRowProps) => {
  const [open, setOpen] = useState(false);
  const id = data.mutationExternalId;

  const fields = data.fields.reduce((acc, item) => {
    acc[item.name.split('.')[item.name.split('.').length - 1]] = item.insurerValue;
    return acc;
  }, {} as Record<string, any>);

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on checkbox or kebab menu
    const target = e.target as HTMLElement;
    if (target.closest('input[type="checkbox"]') || target.closest('[data-kebab-menu]') || target.closest('button')) {
      return;
    }
    setOpen(!open);
  };

  return (
    <>
      <TableRowStyled selected={isSelected} onClick={handleRowClick} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox" sx={{ width: 48 }} onClick={e => e.stopPropagation()}>
          {data.mutationType !== 'UNKNOWN' ? (
            <Checkbox checked={isSelected} onChange={onSelectHandler} />
          ) : (
            <Box width={42} />
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="h6">
            {data.mutationType}
            {data.diffCount > 0 && (
              <Typography component="span" marginLeft={1} variant="caption">
                {`(${data.diffCount})`}
              </Typography>
            )}
          </Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {data.employeeBSN.trim().length > 0 ? data.employeeBSN : fields['bsn']}
        </TableCell>
        <TableCell sx={{ width: '100%' }}>{data.employeeName}</TableCell>
      </TableRowStyled>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: 50 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, paddingLeft: 2 }}>
              <CompareBatchTableInnerRow
                key={`${id}_headers`}
                isRowHeaders={true}
                fieldName={intl.formatMessage({ id: TranslationKeys.global_field })}
                mutationValue={intl.formatMessage({ id: TranslationKeys.global_mutation })}
                insurerValue={intl.formatMessage({ id: TranslationKeys.global_insurer })}
              />

              {(data.fields || []).map((field, idx) => {
                const valueTextColor = field.isDifferent ? 'error' : undefined;

                return (
                  <CompareBatchTableInnerRow
                    key={`${id}_row_${idx}`}
                    fieldName={intl.formatMessage({ id: field.name })}
                    valueTextColor={valueTextColor}
                    mutationValue={getFormattedFieldValue({ type: field.type, value: field.mutationValue })}
                    insurerValue={getFormattedFieldValue({ type: field.type, value: field.insurerValue })}
                  />
                );
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const CompareBatchTable = ({
  batchId,
  connectionId,
  selected,
  onSelect,
  onSearch,
  onFilterMutationType,
  onSortChange,
  sortBy,
  searchQuery,
  batchCompareData,
  approveBatch,
  archiveBatch,
  markAsComplete,
  markSelectedAsComplete,
  resendMutations,
  rejectMutations,
  declineBatch,
  batchStatus,
  paginationProps,
  isLoading = false,
  mutationTypes,
}: CompareBatchTableProps) => {
  const intl = useIntl();
  const { locale } = useLocale();
  const [isConnectionManualInput, setIsConnectionManualInput] = React.useState(false);

  useConnectionGetConnectionQuery({
    variables: { connectionId: connectionId },
    options: {
      enabled: !!connectionId,
      onSuccess: data => {
        const isManualInputOrFileUpload = data.packages.some(connectionPackage =>
          [ConnectionSourcePackageTypeEnum.Manual.toString()].includes(connectionPackage.packageType),
        );
        setIsConnectionManualInput(isManualInputOrFileUpload);
      },
    },
  });

  const onSelectHandler = React.useCallback(
    (id: string) => () => {
      const isSelected = selected.has(id);

      if (isSelected) {
        selected.delete(id);
      } else {
        selected.add(id);
      }
      onSelect(new Set(selected));
    },
    [onSelect, selected],
  );

  const isAllPageSelected = React.useMemo(() => {
    const selectedCount = batchCompareData.reduce<number>((acc, row) => {
      if (selected.has(row.mutationExternalId)) {
        return acc + 1;
      }
      return acc;
    }, 0);

    return batchCompareData.length === selectedCount && batchCompareData.length > 0;
  }, [batchCompareData, selected]);

  const onSelectAll = React.useCallback(() => {
    batchCompareData.forEach(({ mutationExternalId }) => {
      if (isAllPageSelected) {
        selected.delete(mutationExternalId);
      } else {
        selected.add(mutationExternalId);
      }
    });

    onSelect(new Set(selected));
  }, [isAllPageSelected, batchCompareData, onSelect, selected]);

  const getFormattedFieldValue = React.useCallback(
    ({ type, value }: { type: BatchDataFieldType; value: BatchDataFieldValue }) => {
      if (value === null) {
        return '-';
      }
      if (type === 'percentage' && (typeof value === 'string' || typeof value === 'number')) {
        return formatter.percentageFormat({ value, locale });
      }
      if (type === 'date') {
        return formatter.date(value);
      }
      if (type === 'boolean') {
        return formatter.yesNo(value);
      }
      if (type === 'currency' && (typeof value === 'string' || typeof value === 'number')) {
        const result = formatter.currencyFormat({ value, locale });

        return result === '' ? value : result;
      }

      return value;
    },
    [locale],
  );

  const hasData = batchCompareData?.length > 0;
  const showData = !isLoading && hasData;
  const showNoData = !isLoading && !showData;

  const columns: CompareBatchTableColumn[] = React.useMemo(
    () => [
      {
        name: 'mutationType',
        label: <FormattedMessage id={TranslationKeys.mutation_type} />,
        sortable: false,
        whiteSpace: 'nowrap',
      },
      {
        name: 'socialSecurityNumber',
        label: <FormattedMessage id={TranslationKeys.employees_detail_socialSecurityNumber} />,
        sortable: true,
        whiteSpace: 'nowrap',
      },
      {
        name: 'employeeName',
        label: <FormattedMessage id={TranslationKeys.global_lastName} />,
        sortable: true,
        width: '100%',
      },
    ],
    [],
  );

  const handleSortChange = React.useCallback(
    ({ name }: { name: string }) => {
      const currentDirection = sortBy[name] || null;
      let newDirection: TableCellHeaderSortOptions = null;

      if (currentDirection === null) newDirection = 'asc';
      else if (currentDirection === 'asc') newDirection = 'desc';
      else newDirection = null;

      const newSortBy: Record<string, TableCellHeaderSortOptions> = {};
      if (newDirection !== null) {
        newSortBy[name] = newDirection;
      }

      onSortChange(newSortBy);
    },
    [sortBy, onSortChange],
  );

  return (
    <Box data-testid={COMPARE_BATCH_TABLE_DATA_TEST_ID} marginY={2}>
      <CompareBatchTableToolbar
        isLoading={isLoading}
        onSearch={onSearch}
        onFilterMutationType={onFilterMutationType}
        searchQuery={searchQuery}
        approveBatch={approveBatch}
        archiveBatch={archiveBatch}
        markAsComplete={markAsComplete}
        markSelectedAsComplete={markSelectedAsComplete}
        resendMutations={resendMutations}
        rejectMutations={rejectMutations}
        declineBatch={declineBatch}
        batchStatus={batchStatus}
        selectedCount={selected.size}
        batchSize={batchCompareData.length}
        mutationTypes={mutationTypes}
      />

      <TableContainerStyled>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48 }}>
                <Checkbox
                  checked={isAllPageSelected}
                  onChange={onSelectAll}
                  disabled={isLoading || paginationProps?.count === 0}
                />
              </TableCell>
              {columns.map(column => (
                <TableCellHeader
                  key={column.name}
                  name={column.name}
                  arrowPosition={column.sortable ? 'right' : undefined}
                  direction={column.sortable ? sortBy[column.name] || null : undefined}
                  onChange={column.sortable ? handleSortChange : undefined}
                  sx={{ whiteSpace: column.whiteSpace, width: column.width, padding: '8px' }}
                >
                  {column.label}
                </TableCellHeader>
              ))}
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {showData &&
              batchCompareData.map(data => {
                const id = data.mutationExternalId;
                const isSelected = selected.has(id);

                return (
                  <CollapsibleRow
                    key={id}
                    data={data}
                    isSelected={isSelected}
                    onSelectHandler={onSelectHandler(id)}
                    isConnectionManualInput={isConnectionManualInput}
                    getFormattedFieldValue={getFormattedFieldValue}
                    batchId={batchId}
                    connectionId={connectionId}
                    intl={intl}
                    columns={columns}
                  />
                );
              })}
            {showNoData && (
              <NoDataRowStyled>
                <TableCell colSpan={6}>
                  <Typography variant="h6" textAlign="center" padding={2}>
                    {!searchQuery && <FormattedMessage id={TranslationKeys.global_noData} />}
                    {!!searchQuery && (
                      <FormattedMessage
                        id={TranslationKeys.global_searchNotFound_searchQuery}
                        values={{ searchQuery }}
                      />
                    )}
                  </Typography>
                </TableCell>
              </NoDataRowStyled>
            )}
          </TableBody>
        </Table>
      </TableContainerStyled>

      {!!paginationProps && (
        <Box>
          <Pagination {...paginationProps} />
        </Box>
      )}
    </Box>
  );
};
